import { FetchCustomerCustomFieldResponse, FetchCustomFieldResponseDataType, FetchCustomFieldResponseFieldType } from '../../../../admin/models/campaign/fetchCustomFieldsModel';
import { QueryBuilderItem } from "../models";
import { BaseQueryBuilderPayload } from '@bikdotai/bik-component-library';

/**
 * Helper class for managing and transforming custom fields
 * between API responses and query builder items.
 */
export class CustomFieldHelper {

  /**
   * Maps API response data types to query builder data types.
   */
  dataTypeMap: Record<FetchCustomFieldResponseDataType, string>;

  constructor() {
    this.dataTypeMap = {
      [FetchCustomFieldResponseDataType.STRING]: "string",
      [FetchCustomFieldResponseDataType.STRING_ARRAY]: "string",
      [FetchCustomFieldResponseDataType.DATE]: "date",
      [FetchCustomFieldResponseDataType.NUMBER]: "integer",
      [FetchCustomFieldResponseDataType.BOOLEAN]: "boolean",
    }
  }

  /**
   * Transforms API response custom fields into query builder items.
   * @param apiResponse - Array of custom fields from the API.
   * @returns Array of query builder items.
   */
  parseCustomFieldFromApiToQueryBuilder(apiResponse: FetchCustomerCustomFieldResponse[]): QueryBuilderItem[] {
    const queryBuildercustomFields = apiResponse.map((field) => ({
      id: field.id.toString(),
      name: field.fieldName,
      type: this.dataTypeMap[field.dataType],
      description: "",
      needValuesRetrieval: field.options?.length > 0,
      aliasName: field.aliasName,
      customOptions: field.options?.map((option) => ({
        id: option,
        name: option,
        type: 'string',
        description: "",
        needValuesRetrieval: false,
      } as QueryBuilderItem)),
    }));

    return queryBuildercustomFields;
  }

  /**
   * Retrieves custom options for a given field ID.
   * @param id - The ID of the field.
   * @param items - Array of query builder items.
   * @returns Array of custom options for the field.
   */
  getCustomOptionsForId(id: string, items: QueryBuilderItem[]): QueryBuilderItem[] {
    const item = items.find((item) => item.id === id);
    if (item && item.customOptions) {
      return item.customOptions;
    }
    return [];
  }

  /**
   * Searches for custom options by field ID and search key.
   * @param id - The ID of the field.
   * @param searchKey - The search keyword.
   * @param items - Array of query builder items.
   * @returns Filtered array of custom options matching the search key.
   */
  onSearchCustomOptionsForId(id: string, searchKey: string, items: QueryBuilderItem[]): QueryBuilderItem[] {
    const item = items.find((item) => item.id === id);
    if (item && item.customOptions) {
      return item.customOptions.filter((option) =>
        option.name.toLowerCase().includes(searchKey.toLowerCase())
      );
    }
    return [];
  }

  /**
   * Handles custom field property selection and search.
   * @param originalItems - Original array of query builder items.
   * @param userSelection - User selection payload containing property name and search key.
   * @returns Filtered array of query builder items based on user selection.
   */
  hasCustomFieldPropertyHandler(originalItems: QueryBuilderItem[], userSelection: BaseQueryBuilderPayload<{
    propertyName?: string;
    searchKey?: string;
  }>): QueryBuilderItem[] {
    if (userSelection.query?.propertyName && userSelection.query?.searchKey) {
      return this.onSearchCustomOptionsForId(userSelection.query.propertyName, userSelection.query.searchKey, originalItems);
    }

    if (userSelection.query?.propertyName) {
      return this.getCustomOptionsForId(userSelection.query.propertyName, originalItems);
    }

    return originalItems;
  }

  /**
   * Retrieves the name of a custom field by its ID.
   * @param id - The ID of the custom field.
   * @param customFields - Array of query builder items.
   * @returns The name of the custom field, or an empty string if not found.
   */
  getNameForId(id: string, customFields: QueryBuilderItem[]): string {
    const item = customFields.find((item) => item.id === id);
    if (item) {
      return item.name;
    }
    return "";
  }

  /**
   * Finds a custom field by its variable name.
   * @param variableName - The variable name to search for.
   * @param customFieldApiResponse - Array of custom fields from the API.
   * @returns The matching custom field, or undefined if not found.
   */
  getCustomFieldByVariableName(
    variableName: string,
    customFieldApiResponse: FetchCustomerCustomFieldResponse[]
  ): FetchCustomerCustomFieldResponse | undefined {
    return customFieldApiResponse.find((item) => variableName.trim() === `{{customer.custom.${item.aliasName?.trim() ?? ""}}}`);
  }

  /**
   * Determines the data type for a given custom field variable.
   * @param variable - The custom field variable.
   * @returns The data type or field type of the variable.
   */
  getDataTypeForVariable(variable: FetchCustomerCustomFieldResponse) {
    if (variable.dataType === FetchCustomFieldResponseDataType.DATE) {
      return variable.fieldType;
    }
    if (variable.fieldType === FetchCustomFieldResponseFieldType.DROPDOWN) {
      return variable.fieldType;
    }
    return variable.dataType;
  }
}